<template>
  <div
    ref="selectRef"
    class="select"
    :class="{ 'select--invalid': errorMessage, 'select--open': isOpen }"
  >
    <label v-if="label" class="label t-form-label" :for="id">
      {{ label }}
    </label>
    <div class="select-wrapper">
      <button
        :id="id"
        class="selected t-form-text"
        :aria-invalid="!!errorMessage"
        :disabled="disabled"
        @click="toggleSelect"
      >
        <span class="selected__label">
          {{ currentLabel || placeholder || t('select') }}
        </span>
        <span class="icon-wrapper">
          <ChevronIcon class="icon" />
        </span>
      </button>
      <div v-if="isOpen" role="listbox" class="options">
        <div class="options-inner">
          <button
            v-for="(item, i) in items"
            :key="`${item.label}-${i}`"
            class="option t-form-text"
            :class="{ 'option--selected': model === item.value }"
            :aria-selected="model === item.value"
            role="option"
            @click="onSelect(item)"
          >
            <span class="option__label">
              {{ item.label }}
            </span>
            <span class="icon-wrapper">
              <CheckIcon class="icon" />
            </span>
          </button>
        </div>
      </div>
    </div>
    <p v-if="errorMessage" class="t-form-error">{{ errorMessage }}</p>
  </div>
</template>

<script setup lang="ts" generic="TItemValue">
import { onClickOutside, onKeyDown } from '@vueuse/core'

import CheckIcon from '@/assets/icons/check.svg?component'
import ChevronIcon from '@/assets/icons/chevron-down.svg?component'
import type { SelectItem } from '@/types/utils'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    items: SelectItem<TItemValue>[]
    label?: string
    errorMessage?: string
    placeholder?: string
    disabled?: boolean
  }>(),
  {
    label: '',
    errorMessage: '',
    placeholder: ''
  }
)

const model = defineModel<TItemValue>({ default: null })

const selectRef = ref<HTMLDivElement | null>(null)
const isOpen = ref(false)

const id = useId()

const toggleSelect = () => (isOpen.value = !isOpen.value)
const closeSelect = () => (isOpen.value = false)

const onSelect = (item: SelectItem<TItemValue>) => {
  model.value = item.value
  closeSelect()
}

const currentLabel = computed(
  () => props.items.find(item => item.value === model.value)?.label
)

onClickOutside(selectRef, closeSelect)
onKeyDown('Escape', closeSelect)
</script>

<style scoped lang="scss">
$select-border-width: 1px;

@mixin option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: rem(12px) rem(22px);
  color: $global-grey-01;
  text-align: left;
}

.select {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.label {
  .select--invalid & {
    color: $global-error;
  }
}

.select-wrapper {
  position: relative;
  width: 100%;
}

.selected {
  @include option;
  background-color: $global-white;
  border: $select-border-width solid $global-grey-04;
  border-radius: 22px;

  transition:
    background-color $transition,
    border-color $transition;

  &:disabled {
    color: $global-grey-04;
    border-color: $global-grey-05;
  }

  .select--invalid & {
    color: $global-error;
    border-color: $global-error;
  }

  &:not(:disabled) {
    .select--open & {
      border-color: $global-black $global-black transparent $global-black;
      border-radius: 22px 22px 0 0;

      transition: background-color $transition;
    }

    .select--open.select--invalid & {
      border-color: $global-error $global-error transparent $global-error;
    }

    .select:not(.select--invalid, .select--open) &:hover {
      border-color: $global-grey-02;
    }
  }
}

.options {
  position: absolute;
  inset: calc(100% - $select-border-width) 0 auto;
  z-index: z(popup);
  overflow: hidden;
  background-color: $global-white;
  border: $select-border-width solid $global-black;
  border-top: unset;
  border-radius: 0 0 22px 22px;

  .select--invalid & {
    border-color: $global-error;
  }
}

.options-inner {
  max-height: rem(350px);
  overflow-y: auto;
}

.option {
  @include option;
  padding-block: rem(13px);
  transition: background-color $transition;

  &:nth-child(odd) {
    background-color: $global-grey-06;
  }

  &:hover {
    background-color: $global-sun-06;
  }

  .icon-wrapper {
    opacity: 0;

    transition: opacity $transition;
  }

  &--selected {
    .icon-wrapper {
      opacity: 1;
    }
  }
}

.icon {
  display: block;
}
</style>
